import {
  ChangeDetectionStrategy,
  Component,
  computed,
  input,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { GroupResult } from '@progress/kendo-data-query';
import { formatCurrency } from '@ups/xplat/utils';

/**
 * Reusable component for displaying group headers with count and total amounts.
 *
 * Usage in kendoGridGroupHeaderTemplate:
 * ```html
 * <ng-template kendoGridGroupHeaderTemplate let-group let-field="field" let-dataItem let-value="value">
 *   <ups-group-header-with-count
 *     [group]="group"
 *     [field]="field"
 *     [value]="value"
 *     [dataItem]="dataItem"
 *     [fieldTitleMapper]="getFieldTitle"
 *     [amountField]="'amount'"
 *   ></ups-group-header-with-count>
 * </ng-template>
 * ```
 */
@Component({
  selector: 'ups-group-header-with-count',
  imports: [CommonModule],
  templateUrl: './group-header-with-count.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class GroupHeaderWithCountComponent {
  // Inputs for the group header data
  readonly group = input<GroupResult>();
  readonly field = input<string>();
  readonly value = input<string>();
  readonly dataItem = input<Record<string, any>>();

  // Input for custom field title mapping function
  readonly fieldTitleMapper = input<(field: string) => string>(
    (field) => field
  );

  // Input for custom amount field name (defaults to 'amount' for opportunities, 'estimateValue' for quotes)
  readonly amountField = input<string>('amount');

  // Derived as computed() rather than template method calls so the group header
  // does not recompute on every change-detection pass (zoneless-readiness,
  // Feature 31662).
  protected readonly fieldTitle = computed(() =>
    this.fieldTitleMapper()(this.field() ?? '')
  );
  protected readonly formattedCurrency = computed(() =>
    formatCurrency(Number(this.dataItem()?.[this.amountField()] ?? 0))
  );
}
